import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamPage = () => {
  const [team, setTeam] = useState([]);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axios.get(`${API}/team`);
        setTeam(response.data);
      } catch (error) {
        console.error("Error fetching team:", error);
      }
    };
    fetchTeam();
  }, []);

  return (
    <div data-testid="team-page" className="bg-[#F4F4F0] pt-24">
      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-6"
          >
            Our Team
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#1A1A1A] leading-[0.9] mb-8"
          >
            The minds behind
            <br />
            the designs.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl font-light text-[#1A1A1A]/70 leading-relaxed max-w-2xl"
          >
            Our talented team of architects, designers, and project managers brings decades of combined experience to every project.
          </motion.p>
        </div>
      </section>

      {/* Team Grid */}
      <section
        data-testid="team-grid"
        className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {team.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group"
              data-testid={`team-member-${index}`}
            >
              <div className="relative overflow-hidden aspect-[4/5] mb-6">
                <img
                  src={member.image_url}
                  alt={member.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                {member.email && (
                  <div className="absolute bottom-6 left-6 right-6 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                    <a
                      href={`mailto:${member.email}`}
                      className="inline-flex items-center gap-2 text-[#F4F4F0] text-sm hover:underline"
                    >
                      <Mail size={16} />
                      {member.email}
                    </a>
                  </div>
                )}
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#3A4D39] mb-2">
                {member.role}
              </p>
              <h3 className="font-heading text-3xl md:text-4xl text-[#1A1A1A] mb-4">
                {member.name}
              </h3>
              <p className="text-[#1A1A1A]/60 leading-relaxed">
                {member.bio}
              </p>
            </motion.div>
          ))}
        </div>

        {team.length === 0 && (
          <div className="text-center py-20">
            <p className="text-[#1A1A1A]/50">Loading team members...</p>
          </div>
        )}
      </section>

      {/* Culture Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#E5E5E0]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-6">
              Our Culture
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-[#1A1A1A] mb-8">
              Collaboration is at
              <br />
              our core.
            </h2>
          </div>
          <div className="space-y-6">
            <p className="text-lg font-light text-[#1A1A1A]/70 leading-relaxed">
              At IIDAS, we foster an environment where creativity thrives. Our open studio culture encourages collaboration, mentorship, and continuous learning.
            </p>
            <p className="text-lg font-light text-[#1A1A1A]/70 leading-relaxed">
              We believe that the best designs emerge when diverse perspectives come together. Our team includes architects, interior designers, urban planners, and sustainability experts who collaborate on every project.
            </p>
            <p className="text-lg font-light text-[#1A1A1A]/70 leading-relaxed">
              Beyond design excellence, we're committed to nurturing the next generation of architects through our internship and mentorship programs.
            </p>
          </div>
        </div>
      </section>

      {/* Join Us Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#1A1A1A] text-[#F4F4F0]">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] text-[#F4F4F0]/50 mb-6">
            Join Us
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light mb-8">
            We're always looking
            <br />
            for talented individuals.
          </h2>
          <p className="text-lg font-light text-[#F4F4F0]/70 leading-relaxed mb-10">
            If you're passionate about architecture and want to work on meaningful projects, we'd love to hear from you. We offer competitive benefits, professional development opportunities, and a collaborative work environment.
          </p>
          <Link
            to="/contact"
            data-testid="team-careers-btn"
            className="inline-flex items-center gap-3 border border-[#F4F4F0] text-[#F4F4F0] px-8 py-4 uppercase tracking-widest text-xs hover:bg-[#F4F4F0] hover:text-[#1A1A1A] transition-colors duration-300"
          >
            Get in Touch
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
